import {
  Tree as MantineTree,
  TreeProps,
  useTree as useMantineTree,
} from '@mantine/core';
import type { RenderTreeNodePayload, TreeNodeData } from '@mantine/core';
import clsx from 'clsx';
import React from 'react';
import classes from './Tree.module.css';

export type ITreeProps = TreeProps;
export type { RenderTreeNodePayload, TreeNodeData };

type TreeClassNamesFn = Extract<
  NonNullable<ITreeProps['classNames']>,
  (...args: never[]) => unknown
>;

const DefaultProps: Partial<ITreeProps> = {
  levelOffset: 'md',
  expandOnClick: false,
  expandOnSpace: false,
  selectOnClick: false,
};

const isSelectionKey = (key: string) => key === 'Enter' || key === ' ';

/** @registry Design-layer Tree over Mantine Tree with the house look (subtree guide lines, compact rows): rows select, chevrons expand, and Enter/Space activate the focused row so TreeRow's onSelect fires for mouse and keyboard alike. Import this instead of @mantine/core Tree outside src/design. */
export const Tree = React.forwardRef<HTMLUListElement, ITreeProps>(
  ({ classNames, onKeyDown, ...props }, ref) => {
    const houseClassNames: TreeClassNamesFn = (theme, treeProps, ctx) => {
      const consumer: ReturnType<TreeClassNamesFn> =
        typeof classNames === 'function'
          ? classNames(theme, treeProps, ctx)
          : classNames ?? {};

      return {
        root: clsx(classes.root, consumer.root),
        node: clsx(classes.node, consumer.node),
        subtree: clsx(classes.subtree, consumer.subtree),
        label: clsx(classes.label, consumer.label),
      };
    };

    // Focus lives on the treeitem, not on the rendered row, so a row's own
    // handlers never see the key press; forward it to the row as a click.
    const handleKeyDown = (event: React.KeyboardEvent<HTMLUListElement>) => {
      onKeyDown?.(event);
      if (event.defaultPrevented || !isSelectionKey(event.key)) return;

      const row = (event.target as HTMLElement)
        .closest('[role="treeitem"]')
        ?.querySelector<HTMLElement>('[data-tree-row]');
      if (!row) return;

      event.preventDefault();
      row.click();
    };

    return (
      <MantineTree
        {...DefaultProps}
        {...props}
        classNames={houseClassNames}
        onKeyDown={handleKeyDown}
        ref={ref}
      />
    );
  },
);

Tree.displayName = 'Tree';

/** @registry Tree selection/expansion controller for the design-layer Tree; import this instead of @mantine/core useTree outside src/design. */
export const useTree = useMantineTree;
